import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { getDashboardInfo, getLoginsInPeriod, getNewArtworksInPeriod, getRecentActivities, getSearchesInPeriod } from '../../services/api';

// ─── Dados estáticos ────────────────────────────────────────────────────────

const totalCards = [
  {
    label: 'Total de obras',
    description: 'Obras catalogadas no sistema',
    color: '#185FA5',
    bg: '#E6F1FB',
    Icon: LibraryBooksIcon,
    key: 'totalObras',
  },
  {
    label: 'Fichas catalográficas',
    description: 'Tipos de fichas disponíveis',
    color: '#534AB7',
    bg: '#EEEDFE',
    Icon: DescriptionIcon,
    key: 'totalFichas',
  },
  {
    label: 'Usuários cadastrados',
    description: 'Pessoas com acesso ao sistema',
    color: '#3B6D11',
    bg: '#EAF3DE',
    Icon: PeopleIcon,
    key: 'totalUsuarios',
  },
];

const activityBase = [
  {
    label: 'Novas obras',
    base30: 32,
    prefix: '+',
    Icon: TrendingUpIcon,
    iconColor: '#B45309',
    iconBg: '#FEF3C7',
  },
  {
    label: 'Acessos ao acervo',
    base30: 5678,
    prefix: '',
    Icon: VisibilityIcon,
    iconColor: '#0F766E',
    iconBg: '#CCFBF1',
  },
  {
    label: 'Pesquisas realizadas',
    base30: 1892,
    prefix: '',
    Icon: SearchIcon,
    iconColor: '#7C3AED',
    iconBg: '#EDE9FE',
  },
];

const PRESETS = [
  { label: '7 dias',        days: 7 },
  { label: '30 dias',       days: 30 },
  { label: '3 meses',       days: 90 },
  { label: '6 meses',       days: 180 },
  { label: '1 ano',         days: 365 },
  { label: 'Personalizado', days: null },
];

// ─── Utilitários de data ─────────────────────────────────────────────────────

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function toInputDate(date) {
  return date.toISOString().slice(0, 10);
}

function formatBadge(from, to) {
  const short = { day: 'numeric', month: 'short' };
  const long  = { day: 'numeric', month: 'short', year: 'numeric' };
  return `${from.toLocaleDateString('pt-BR', short)} – ${to.toLocaleDateString('pt-BR', long)}`;
}

function presetRange(days) {
  const to   = new Date();
  const from = addDays(to, -days + 1);
  return { from, to };
}

function daysBetween(from, to) {
  return Math.max(1, Math.round((to - from) / 86400000) + 1);
}

function scaleValue(base30, days) {
  return Math.round(base30 * days / 30).toLocaleString('pt-BR');
}

// ─── Estilos compartilhados ──────────────────────────────────────────────────

const activeStyle = {
  background: '#E1F5EE',
  border: '1px solid #9FE1CB',
  color: '#085041',
  fontWeight: 500,
};

const inactiveStyle = {
  background: 'transparent',
  border: '1px solid #D8D8D8',
  color: '#666',
  fontWeight: 400,
};

const inputStyle = {
  border: '1px solid #D8D8D8',
  borderRadius: 6,
  fontSize: '0.93em',
  padding: '5px 10px',
  outline: 'none',
  color: '#333',
  fontFamily: 'inherit',
};

// ─── Componentes ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, description, color, bg, Icon }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: '20px 24px',
        borderRadius: '12px',
        border: '1px solid #E8E8E8',
        background: '#fff',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography sx={{ fontSize: '0.85em', color: 'text.secondary', lineHeight: 1.4 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.85em', fontWeight: 500, lineHeight: 1.2, color: '#111' }}>
          {value === null ? <Skeleton width={48} /> : value}
        </Typography>
        <Typography sx={{ fontSize: '0.85em', color: 'text.secondary', lineHeight: 1.4 }}>
          {description}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ml: 2,
          mt: '2px',
        }}
      >
        <Icon sx={{ color, fontSize: '1.3em' }} />
      </Box>
    </Paper>
  );
}

function PeriodFilter({ onChange }) {
  const initial = presetRange(30);

  const [active, setActive]         = useState('30 dias');
  const [showCustom, setShowCustom] = useState(false);
  const [range, setRange]           = useState(initial);
  const [customFrom, setCustomFrom] = useState(toInputDate(initial.from));
  const [customTo, setCustomTo]     = useState(toInputDate(initial.to));

  const handlePreset = (preset) => {
    setActive(preset.label);
    if (preset.days === null) {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    const next = presetRange(preset.days);
    setRange(next);
    onChange?.(next);
  };

  const handleApply = () => {
    if (customFrom > customTo) return;
    const next = { from: new Date(customFrom), to: new Date(customTo) };
    setRange(next);
    onChange?.(next);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E8E8E8',
        borderRadius: '8px',
        background: '#fff',
        px: '20px',
        py: '14px',
        mb: 3,
      }}
    >
      {/* Linha 1 — controles principais */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <CalendarMonthIcon sx={{ fontSize: '1.14em', color: 'text.disabled' }} />

        <Typography sx={{ fontSize: '0.93em', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          Período:
        </Typography>

        <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {PRESETS.map((preset) => (
            <button
              key={preset.label}
              onClick={() => handlePreset(preset)}
              style={{
                cursor: 'pointer',
                borderRadius: 6,
                fontSize: '0.93em',
                padding: '4px 10px',
                transition: 'all 0.15s',
                ...(active === preset.label ? activeStyle : inactiveStyle),
              }}
            >
              {preset.label}
            </button>
          ))}
        </Box>

        <Box
          sx={{
            ml: 'auto',
            px: '10px',
            py: '3px',
            background: '#E1F5EE',
            border: '1px solid #9FE1CB',
            borderRadius: '6px',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography sx={{ fontSize: '0.85em', color: '#085041', fontWeight: 500 }}>
            {formatBadge(range.from, range.to)}
          </Typography>
        </Box>
      </Box>

      {/* Linha 2 — range personalizado */}
      {showCustom && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '10px', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.93em', color: 'text.secondary' }}>De</Typography>
          <input
            type="date"
            value={customFrom}
            max={customTo}
            onChange={(e) => setCustomFrom(e.target.value)}
            style={inputStyle}
          />
          <Typography sx={{ fontSize: '0.93em', color: 'text.secondary' }}>Até</Typography>
          <input
            type="date"
            value={customTo}
            min={customFrom}
            onChange={(e) => setCustomTo(e.target.value)}
            style={inputStyle}
          />
          <button
            onClick={handleApply}
            style={{ cursor: 'pointer', borderRadius: 6, fontSize: '0.93em', padding: '5px 14px', ...activeStyle }}
          >
            Aplicar
          </button>
        </Box>
      )}
    </Paper>
  );
}

function ActivityCard({ label, value, prefix, Icon, iconColor, iconBg }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: '20px 24px',
        borderRadius: '12px',
        border: '1px solid #9FE1CB',
        background: '#E1F5EE',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography sx={{ fontSize: '0.85em', color: '#085041', lineHeight: 1.4 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.85em', fontWeight: 500, lineHeight: 1.2, color: '#053327' }}>
          {value === null ? <Skeleton width={60} /> : <>{prefix}{value}</>}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ml: 2,
          mt: '2px',
        }}
      >
        <Icon sx={{ color: iconColor, fontSize: '1.3em' }} />
      </Box>
    </Paper>
  );
}

// ─── Utilitários de atividades ───────────────────────────────────────────────

const DOT_COLORS = { create: '#22C55E', update: '#EAB308', delete: '#EF4444' };
function dotColor(type) { return DOT_COLORS[type] ?? '#9E9E9E'; }

const ACTIVITY_LABELS = {
  'create:artwork': 'Nova obra',
  'update:artwork': 'Edição',
  'delete:artwork': 'Exclusão',
  'create:user':    'Novo usuário',
  'update:user':    'Edição de usuário',
  'delete:user':    'Usuário removido',
  'create:font':    'Nova ficha',
  'update:font':    'Edição de ficha',
  'delete:font':    'Ficha removida',
};
function activityLabel(type, entity) { return ACTIVITY_LABELS[`${type}:${entity}`] ?? type; }

// ─── Utilitário de tempo relativo ────────────────────────────────────────────

function relativeTime(timestamp) {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const mins   = Math.floor(diffMs / 60000);
  if (mins < 1)   return 'agora mesmo';
  if (mins < 60)  return `há ${mins} minuto${mins > 1 ? 's' : ''}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `há ${hrs} hora${hrs > 1 ? 's' : ''}`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `há ${days} dia${days > 1 ? 's' : ''}`;
  const months = Math.floor(days / 30);
  return `há ${months} ${months > 1 ? 'meses' : 'mês'}`;
}

// ─── Componentes de painéis ───────────────────────────────────────────────────

function RecentActivities() {
  const [activities, setActivities] = useState(null);

  useEffect(() => {
    getRecentActivities().then(({ data }) => setActivities(data));
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid #E8E8E8', borderRadius: '12px', background: '#fff', p: '20px 24px', height: '100%' }}
    >
      <Typography sx={{ fontSize: '0.78em', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled', mb: 2 }}>
        Atividades recentes
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {activities === null
          ? [1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#E0E0E0', mt: '4px', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" height={18} />
                  <Skeleton width="90%" height={15} />
                </Box>
              </Box>
            ))
          : activities.length === 0
          ? (
              <Typography sx={{ fontSize: '0.93em', color: 'text.disabled' }}>Nenhuma atividade registrada.</Typography>
            )
          : activities.map((item) => (
              <Box key={item._id} sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: dotColor(item.type), mt: '6px', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Typography sx={{ fontSize: '0.85em', fontWeight: 600, color: '#111' }}>
                      {activityLabel(item.type, item.entity)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78em', color: 'text.disabled', whiteSpace: 'nowrap', ml: 1 }}>
                      {relativeTime(item.timestamp)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.85em', color: 'text.secondary' }}>{item.description}</Typography>
                </Box>
              </Box>
            ))
        }
      </Box>
    </Paper>
  );
}

function FichaDistribution({ data }) {
  const items = (() => {
    if (!data) return null;
    const total = data.reduce((sum, a) => sum + (a.count ?? 0), 0);
    const max   = Math.max(...data.map((a) => a.count ?? 0), 1);
    return data
      .filter((a) => (a.count ?? 0) > 0)
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
      .map((a) => ({
        name:  a.font,
        count: a.count ?? 0,
        bar:   Math.round(((a.count ?? 0) / max) * 100),
        pct:   total > 0 ? Math.round(((a.count ?? 0) / total) * 100) : 0,
      }));
  })();

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid #E8E8E8', borderRadius: '12px', background: '#fff', p: '20px 24px', height: '100%' }}
    >
      <Typography sx={{ fontSize: '0.78em', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled', mb: 2 }}>
        Distribuição por tipo de ficha
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {items === null
          ? [1, 2, 3].map((i) => (
              <Box key={i}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '6px' }}>
                  <Skeleton width={160} />
                  <Skeleton width={70} />
                </Box>
                <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 3 }} />
              </Box>
            ))
          : items.map((item) => (
              <Box key={item.name}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '6px' }}>
                  <Typography sx={{ fontSize: '0.93em', color: '#111', textTransform: 'capitalize' }}>{item.name}</Typography>
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Typography sx={{ fontSize: '0.85em', color: 'text.secondary' }}>{item.count.toLocaleString('pt-BR')} obras</Typography>
                    <Typography sx={{ fontSize: '0.85em', fontWeight: 600, color: '#4a9e8e', minWidth: 32, textAlign: 'right' }}>{item.pct}%</Typography>
                  </Box>
                </Box>
                <Box sx={{ height: 6, borderRadius: 3, background: '#F0F0F0', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${item.bar}%`, borderRadius: 3, background: '#4a9e8e' }} />
                </Box>
              </Box>
            ))
        }
      </Box>
    </Paper>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function Reports() {
  const [metrics, setMetrics]       = useState({ totalObras: null, totalFichas: null, totalUsuarios: null });
  const [fichaData, setFichaData]   = useState(null);
  const [period, setPeriod]         = useState(presetRange(30));
  const [novasObras, setNovasObras]     = useState(null);
  const [acessos, setAcessos]           = useState(null);
  const [pesquisas, setPesquisas]       = useState(null);

  useEffect(() => {
    document.title = 'CEDOMCA | Relatórios';
    getDashboardInfo().then(({ data }) => {
      setMetrics({
        totalObras:    data.artworks.reduce((sum, a) => sum + (a.count ?? 0), 0),
        totalFichas:   data.artworks.length,
        totalUsuarios: data.users.count,
      });
      setFichaData(data.artworks);
    });
  }, []);

  useEffect(() => {
    setNovasObras(null);
    setAcessos(null);
    setPesquisas(null);
    Promise.all([
      getNewArtworksInPeriod(period.from, period.to),
      getLoginsInPeriod(period.from, period.to),
      getSearchesInPeriod(period.from, period.to),
    ]).then(([obras, logins, searches]) => {
      setNovasObras(obras.data.count);
      setAcessos(logins.data.count);
      setPesquisas(searches.data.count);
    });
  }, [period]);

  const resolvedCards    = totalCards.map((card) => ({ ...card, value: metrics[card.key] }));
  const days             = daysBetween(period.from, period.to);
  const resolvedActivity = activityBase.map((card) => {
    if (card.label === 'Novas obras')          return { ...card, value: novasObras === null ? null : novasObras.toLocaleString('pt-BR') };
    if (card.label === 'Acessos ao acervo')    return { ...card, value: acessos === null ? null : acessos.toLocaleString('pt-BR') };
    if (card.label === 'Pesquisas realizadas') return { ...card, value: pesquisas === null ? null : pesquisas.toLocaleString('pt-BR') };
    return { ...card, value: scaleValue(card.base30, days) };
  });

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" fontWeight={700}>Relatórios e Estatísticas</Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Visão geral do sistema CEDOMCA
      </Typography>

      <Typography
        sx={{
          fontSize: '0.78em',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.disabled',
          mb: 1.5,
        }}
      >
        Totais gerais do acervo
      </Typography>

      <Grid container spacing={2}>
        {resolvedCards.map((card) => (
          <Grid item xs={12} sm={4} key={card.label}>
            <MetricCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Typography
        sx={{
          fontSize: '0.78em',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.disabled',
          mt: 4,
          mb: 1.5,
        }}
      >
        Atividade no período
      </Typography>

      <PeriodFilter onChange={setPeriod} />

      <Grid container spacing={2}>
        {resolvedActivity.map((card) => (
          <Grid item xs={12} sm={4} key={card.label}>
            <ActivityCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <RecentActivities />
        </Grid>
        <Grid item xs={12} md={6}>
          <FichaDistribution data={fichaData} />
        </Grid>
      </Grid>
    </Box>
  );
}
